import { useTranslation } from "react-i18next"

import type { LibraryEditor } from "./use-library-editor"

import { FileText } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

import { EditorSectionCard, FieldRow, SwitchRow } from "./editor-fields"
import { LibraryFilthSources } from "./library-filth-sources-picker"

type BasicsSectionProps = {
  editor: LibraryEditor
}

export function BasicsSection({ editor }: BasicsSectionProps) {
  const { t } = useTranslation()
  const { values, errors, mode, filthBackends, filthUsesPerUserSpaces } = editor
  const isFilth = values.search_backend === "filth"

  return (
    <EditorSectionCard
      icon={<FileText className="size-4" />}
      id="library-basics"
      open={editor.openSections.basics}
      title={t("adminDashboard.libraries.editor.sections.basics", "Basics")}
      onOpenChange={(open) => editor.setSectionOpen("basics", open)}
    >
      <FieldRow
        required
        error={errors.display_name}
        id="display_name"
        label={t("adminDashboard.libraries.editor.displayName", "Display name")}
      >
        <Input
          id="display_name"
          value={values.display_name}
          placeholder={t(
            "adminDashboard.libraries.editor.displayNamePlaceholder",
            "e.g., Engineering Documents"
          )}
          onChange={(event) => editor.handleDisplayNameChange(event.target.value)}
        />
      </FieldRow>

      <FieldRow
        error={errors.id}
        id="id"
        label={t("adminDashboard.libraries.editor.id", "Library ID")}
        required={mode === "create"}
        help={
          mode === "create"
            ? t(
                "adminDashboard.libraries.editor.idHelp",
                "Unique identifier for this library. Use lowercase letters, numbers, and hyphens only."
              )
            : t(
                "adminDashboard.libraries.editor.idLocked",
                "Library ID cannot be changed after creation."
              )
        }
      >
        <Input
          className={mode === "edit" ? "bg-muted text-muted-foreground" : undefined}
          disabled={mode === "edit"}
          id="id"
          value={values.id}
          placeholder={t(
            "adminDashboard.libraries.editor.idPlaceholder",
            "e.g., engineering-documents"
          )}
          onChange={(event) => editor.handleIdChange(event.target.value)}
        />
      </FieldRow>

      <FieldRow
        id="description"
        label={t("adminDashboard.libraries.editor.description", "Description")}
      >
        <Textarea
          id="description"
          value={values.description}
          placeholder={t(
            "adminDashboard.libraries.editor.descriptionPlaceholder",
            "Describe the contents of this library..."
          )}
          onChange={(event) => editor.update({ description: event.target.value })}
        />
      </FieldRow>

      <FieldRow
        id="search_backend"
        label={t("adminDashboard.libraries.editor.searchBackend", "Search backend")}
      >
        <Select
          value={values.search_backend}
          onValueChange={(value) =>
            editor.update({ search_backend: value === "filth" ? "filth" : "azure-search" })
          }
        >
          <SelectTrigger id="search_backend">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="azure-search">
              {t("adminDashboard.libraries.editor.backendAzure", "Azure AI Search")}
            </SelectItem>
            <SelectItem value="filth">
              {t("adminDashboard.libraries.editor.backendFilth", "Prism")}
            </SelectItem>
          </SelectContent>
        </Select>
      </FieldRow>

      {isFilth && (
        <>
          <FieldRow
            required
            error={errors.filth_backend_id}
            id="filth_backend_id"
            label={t("adminDashboard.libraries.editor.filthBackend", "Prism backend")}
          >
            <Select
              value={values.filth_backend_id}
              onValueChange={(value) => {
                const backend = filthBackends?.find((candidate) => candidate.id === value)
                editor.update({
                  filth_backend_id: value,
                  ...(backend?.consumer_mode === "primary" ? { filth_scope: "" } : {}),
                })
              }}
            >
              <SelectTrigger id="filth_backend_id">
                <SelectValue
                  placeholder={t(
                    "adminDashboard.libraries.editor.filthBackendPlaceholder",
                    "Select a Prism backend"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {(filthBackends ?? []).map((backend) => (
                  <SelectItem key={backend.id} value={backend.id}>
                    {backend.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldRow>
          {filthUsesPerUserSpaces ? (
            <>
              <LibraryFilthSources
                backendId={values.filth_backend_id}
                selected={values.filth_source_uids}
                onChange={(uids) => editor.update({ filth_source_uids: uids })}
              />
              <div className="rounded-md border p-3">
                <div className="text-sm font-medium">
                  {t(
                    "adminDashboard.libraries.addDialog.perUserSpaces",
                    "Per-user knowledge spaces"
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  {t(
                    "adminDashboard.libraries.addDialog.perUserSpacesHint",
                    "Prism filters company, team, personal, and source access for the signed-in user."
                  )}
                </p>
              </div>
            </>
          ) : (
            <>
              <FieldRow
                id="filth_scope"
                label={t("adminDashboard.libraries.editor.filthScope", "Scope (optional subtree)")}
              >
                <Input
                  id="filth_scope"
                  placeholder="/mnt/share/engineering"
                  value={values.filth_scope}
                  onChange={(event) => editor.update({ filth_scope: event.target.value })}
                />
              </FieldRow>
              <SwitchRow
                checked={values.company_wide}
                id="company_wide"
                label={t("adminDashboard.libraries.editor.companyWide", "Company-wide content")}
                help={t(
                  "adminDashboard.libraries.editor.companyWideHelp",
                  "Required to bind a library to Prism: content is readable by everyone in the tenant (no per-document ACL)."
                )}
                onCheckedChange={(checked) => editor.update({ company_wide: checked })}
              />
            </>
          )}
        </>
      )}
      {errors.filth_source_uids && (
        <div className="space-y-2">
          <p className="text-destructive text-sm" role="alert">
            {errors.filth_source_uids}
          </p>
          {values.filth_source_uids.length > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => editor.update({ filth_source_uids: [] })}
            >
              {t("adminDashboard.libraries.editor.sources.clear", "Clear sources")}
            </Button>
          )}
        </div>
      )}
    </EditorSectionCard>
  )
}
